import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Search, User } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Select from '../../components/Select';
import LoadingSpinner from '../../components/LoadingSpinner';
import { getUsers, createSupportTicket, getSupportTicketTags } from '../../utils/api';
import toast from 'react-hot-toast';

export default function CreateTicket() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const preselectedUserId = searchParams.get('userId');

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);

    // Form state
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState('medium');
    const [selectedTags, setSelectedTags] = useState([]);

    // Fetch tags
    const { data: tagsData } = useQuery({
        queryKey: ['support-ticket-tags'],
        queryFn: getSupportTicketTags,
    });

    const tags = tagsData?.data || [];

    // If preselected user, fetch their details
    const { data: preselectedUserData } = useQuery({
        queryKey: ['user', preselectedUserId],
        queryFn: () => getUsers({ search: preselectedUserId }),
        enabled: !!preselectedUserId && !selectedUser,
        onSuccess: (data) => {
            const user = data?.data?.users?.find(u => u._id === preselectedUserId);
            if (user) {
                setSelectedUser(user);
            }
        }
    });

    // Search users
    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const response = await getUsers({ search: searchQuery, limit: 10 });
            setSearchResults(response?.data?.users || []);
        } catch (error) {
            console.error('Search failed:', error);
            toast.error('Failed to search users');
        } finally {
            setIsSearching(false);
        }
    };

    // Create ticket mutation
    const createMutation = useMutation({
        mutationFn: () => createSupportTicket({
            targetUserId: selectedUser._id,
            subject,
            description,
            priority,
            tags: selectedTags,
        }),
        onSuccess: (data) => {
            toast.success('Ticket created successfully');
            navigate(`/support/${data?.data?._id || ''}`);
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to create ticket');
        }
    });

    const toggleTag = (tagName) => {
        setSelectedTags((prev) =>
            prev.includes(tagName)
                ? prev.filter(t => t !== tagName)
                : [...prev, tagName]
        );
    };

    const isFormValid = selectedUser && subject.trim() && description.trim();

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="secondary" onClick={() => navigate('/support')}>
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-3xl font-bold">Create Support Ticket</h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        Create a new support ticket for a user
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Form */}
                <div className="lg:col-span-2 space-y-6">
                    {/* User Selection */}
                    <Card title="Select User">
                        {selectedUser ? (
                            <div className="flex items-center justify-between p-4 bg-accent-light dark:bg-accent-dark rounded-lg">
                                <div className="flex items-center gap-3">
                                    {selectedUser.profilePicture ? (
                                        <img
                                            src={selectedUser.profilePicture}
                                            alt=""
                                            className="w-12 h-12 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-border-light dark:bg-border-dark flex items-center justify-center">
                                            <span className="text-lg font-bold">
                                                {selectedUser.username?.charAt(0)?.toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-medium">@{selectedUser.username}</p>
                                        <p className="text-sm text-muted-light dark:text-muted-dark">
                                            {selectedUser.displayName}
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={() => setSelectedUser(null)}
                                >
                                    Change
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Search by username or email..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                        className="flex-1"
                                    />
                                    <Button
                                        variant="secondary"
                                        onClick={handleSearch}
                                        loading={isSearching}
                                    >
                                        <Search size={18} />
                                    </Button>
                                </div>

                                {searchResults.length > 0 && (
                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                        {searchResults.map((user) => (
                                            <button
                                                key={user._id}
                                                onClick={() => {
                                                    setSelectedUser(user);
                                                    setSearchResults([]);
                                                    setSearchQuery('');
                                                }}
                                                className="w-full flex items-center gap-3 p-3 bg-accent-light dark:bg-accent-dark rounded-lg hover:bg-border-light dark:hover:bg-border-dark transition-colors text-left"
                                            >
                                                {user.profilePicture ? (
                                                    <img
                                                        src={user.profilePicture}
                                                        alt=""
                                                        className="w-10 h-10 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-border-light dark:bg-border-dark flex items-center justify-center">
                                                        <User size={20} />
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-medium">@{user.username}</p>
                                                    <p className="text-sm text-muted-light dark:text-muted-dark">
                                                        {user.displayName}
                                                    </p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>

                    {/* Ticket Details */}
                    <Card title="Ticket Details">
                        <div className="space-y-4">
                            <Input
                                label="Subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Brief summary of the issue..."
                                required
                            />

                            <Textarea
                                label="Description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Detailed description of the issue or request..."
                                rows={6}
                                required
                            />
                        </div>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Priority */}
                    <Card title="Priority">
                        <Select
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                        >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                        </Select>
                    </Card>

                    {/* Tags */}
                    <Card title="Tags">
                        <div className="flex flex-wrap gap-2">
                            {tags.map((tag) => (
                                <button
                                    key={tag.name}
                                    onClick={() => toggleTag(tag.name)}
                                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${selectedTags.includes(tag.name)
                                        ? 'ring-2 ring-offset-2 ring-blue'
                                        : 'opacity-60 hover:opacity-100'
                                        }`}
                                    style={{
                                        backgroundColor: `${tag.color}20`,
                                        color: tag.color
                                    }}
                                >
                                    {tag.label}
                                </button>
                            ))}
                        </div>
                        {tags.length === 0 && (
                            <p className="text-sm text-muted-light dark:text-muted-dark">
                                No tags available.{' '}
                                <button
                                    onClick={() => navigate('/support/tags')}
                                    className="text-blue hover:underline"
                                >
                                    Create tags
                                </button>
                            </p>
                        )}
                    </Card>

                    {/* Actions */}
                    <Card>
                        <div className="space-y-3">
                            <Button
                                className="w-full"
                                onClick={() => createMutation.mutate()}
                                loading={createMutation.isPending}
                                disabled={!isFormValid}
                            >
                                Create Ticket
                            </Button>
                            <Button
                                variant="secondary"
                                className="w-full"
                                onClick={() => navigate('/support')}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}

